import { EARTH_RADIUS_IN_KM } from "./calculateDistanceInKmBetweenCoordinates.const";
import { Position } from "geojson";
import { point } from "@turf/helpers";
import pointOnLine from "@turf/point-on-line";
import { hikingGeoJSON, bikingGeoJSON } from "./geojsons";

type Point = [number, number];
type Line = Point[];

function customLineSlice(
  startPoint: Point,
  endPoint: Point,
  line: Line,
  type: "biking" | "hiking"
): Line {
  const start = findClosestPointOnGeoJSON(startPoint[0], startPoint[1], type);
  const end = findClosestPointOnGeoJSON(endPoint[0], endPoint[1], type);

  const startIndex = line.findIndex(
    p => p[0] === start[0] && p[1] === start[1]
  );
  const endIndex = line.findIndex(p => p[0] === end[0] && p[1] === end[1]);

  if (startIndex === -1 || endIndex === -1) {
    throw new Error("Start or end point not found on the line");
  }

  return line.slice(
    Math.min(startIndex, endIndex),
    Math.max(startIndex, endIndex) + 1
  );
}

export function calculateDistanceInKmBetweenCoordinates(
  [longitude1, latitude1]: [number, number],
  [longitude2, latitude2]: [number, number]
) {
  const latitudeDistanceInDegrees = degreesToRadians(latitude2 - latitude1);
  const longitudeDistanceInDegrees = degreesToRadians(longitude2 - longitude1);

  latitude1 = degreesToRadians(latitude1);
  latitude2 = degreesToRadians(latitude2);

  const angle =
    Math.sin(latitudeDistanceInDegrees / 2) *
      Math.sin(latitudeDistanceInDegrees / 2) +
    Math.sin(longitudeDistanceInDegrees / 2) *
      Math.sin(longitudeDistanceInDegrees / 2) *
      Math.cos(latitude1) *
      Math.cos(latitude2);

  const chord = 2 * Math.atan2(Math.sqrt(angle), Math.sqrt(1 - angle));

  return EARTH_RADIUS_IN_KM * chord;
}

export function calculateLineLength(points: [number, number][]): number {
  if (points.length < 2) {
    return 0;
  }

  let totalDistance = 0;

  for (let i = 1; i < points.length; i++) {
    const [lon1, lat1] = points[i - 1];
    const [lon2, lat2] = points[i];
    totalDistance += calculateDistanceInKmBetweenCoordinates(
      [lat1, lon1],
      [lat2, lon2]
    );
  }

  return totalDistance;
}

export function findClosestPointOnGeoJSON(
  a: number,
  b: number,
  type: "biking" | "hiking"
) {
  const targetPoint = point([a, b]);
  const features =
    type === "biking" ? bikingGeoJSON.features[0] : hikingGeoJSON.features[0];
  const nearestPoint = pointOnLine(features as any, targetPoint);
  return nearestPoint.geometry.coordinates;
}

async function getServiceNavigation(start: Position, end: Position) {
  try {
    const data = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/walking/${start[0]},${start[1]};${end[0]},${end[1]}?access_token=${process.env.NEXT_PUBLIC_MAXBOX_TOKEN || "pk.eyJ1IjoiLWthdHJpbmU4NiIsImEiOiJjbHhuM2o2Z3QwZW9tMnJzZXdyZ3d6bjVkIn0.myE9RFFZikeU-1cB0Hb69w"}&geometries=geojson`
    );
    const json = await data.json();

    return {
      distance: parseFloat((json.routes[0].distance / 1000).toFixed(2)),
      coordinates: json.routes[0].geometry.coordinates as Position[],
    };
  } catch {
    return null;
  }
}

interface IGetNavigation {
  coordinates: {
    startPosition: Position;
    endPosition: Position;
  };
  type: "biking" | "hiking";
}

function isValidLine(coordinates: unknown): coordinates is Line {
  return (
    Array.isArray(coordinates) &&
    coordinates.every(
      coord =>
        Array.isArray(coord) &&
        coord.length >= 2 &&
        typeof coord[0] === "number" &&
        typeof coord[1] === "number"
    )
  );
}

function ensureValidLine(coordinates: unknown): Line {
  if (!isValidLine(coordinates)) {
    throw new Error(
      "Invalid input: must be an array of coordinates with at least 2 numeric elements each"
    );
  }

  return coordinates.map(coord => [coord[0], coord[1]]);
}

export async function getNavigation({
  coordinates: { startPosition, endPosition },
  type,
}: IGetNavigation): Promise<{
  distance: number | null;
  coordinates: Position[];
} | null> {
  const entryPoint = findClosestPointOnGeoJSON(
    startPosition[0],
    startPosition[1],
    type
  );

  const exitPoint = findClosestPointOnGeoJSON(
    endPosition[0],
    endPosition[1],
    type
  );

  const rawLineCoordinates: number[][] =
    type === "biking"
      ? bikingGeoJSON.features[0].geometry.coordinates.flat()
      : hikingGeoJSON.features[0].geometry.coordinates.flat();

  const lineCoordinates: Line = ensureValidLine(rawLineCoordinates);

  try {
    const slice = customLineSlice(
      [startPosition[0], startPosition[1]],
      [endPosition[0], endPosition[1]],
      lineCoordinates,
      type
    );

    const [entryPart, exitPart] = await Promise.all([
      getServiceNavigation(entryPoint, startPosition),
      getServiceNavigation(endPosition, exitPoint),
    ]);

    const connection: Position[] = slice;

    if (!entryPart || !exitPart)
      throw Error("Unable to calculate the track via the specified path.");

    const navigation = {
      distance: parseFloat(
        (
          entryPart.distance +
          calculateLineLength(slice) +
          exitPart.distance
        ).toFixed(2)
      ),
      coordinates:
        startPosition[1] < endPosition[1]
          ? exitPart.coordinates
              .concat(connection)
              .concat(entryPart.coordinates)
          : exitPart.coordinates
              .concat(connection.reverse())
              .concat(entryPart.coordinates),
    };

    return navigation;
  } catch (e) {
    return await getServiceNavigation(startPosition, endPosition);
  }
}

export async function getDistanceBetweenCoordinatesWithPathAsync(
  [longitude1, latitude1]: [number, number],
  [longitude2, latitude2]: [number, number]
): Promise<[number, Position[] | null]> {
  try {
    const data = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/walking/${longitude1},${latitude1};${longitude2},${latitude2}?access_token=${process.env.NEXT_PUBLIC_MAXBOX_TOKEN || "pk.eyJ1IjoiLWthdHJpbmU4NiIsImEiOiJjbHhuM2o2Z3QwZW9tMnJzZXdyZ3d6bjVkIn0.myE9RFFZikeU-1cB0Hb69w"}&geometries=geojson`
    );
    const json = await data.json();
    const distance_data = json.routes[0].distance / 1000;
    const coordinates = json.routes[0].geometry.coordinates as Position[];
    return [distance_data, coordinates];
  } catch {
    const distance_value = calculateDistanceInKmBetweenCoordinates(
      [longitude1, latitude1],
      [longitude2, latitude2]
    );
    return [distance_value, null];
  }
}

function degreesToRadians(degrees: number) {
  return (degrees * Math.PI) / 180;
}
